import PropTypes from "prop-types";
import React from "react";

import { MEDIUM, SMALL, useDeviceInfo } from "@swa-ui/browser";
import { Image, PageGrid } from "@swa-ui/core";
import { classNames } from "@swa-ui/string";

import { applyOverrides } from "../applyOverrides";
import { CallToAction } from "../CallToAction";
import { HtmlValue } from "../HtmlValue";
import styles from "./BannerLayout1.module.scss";
import {
  backgroundPropType,
  callToActionPropType,
  getBackgroundStyle,
  graphicPropType,
  renderText,
  textPropType,
} from "./BannerUtils";

/**
 * Renders Banner placement with layoutType as layout1.
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export const BannerLayout1 = (props) => {
  const { additionalProps: { fullWidth = true } = {}, placement, placementData } = props;
  const { background, callToAction, disclaimer, graphic, text } = placement ?? {};
  const { screenSize } = useDeviceInfo();
  const { align } = applyOverrides(graphic, screenSize);

  return (
    <div className={styles.container} style={getBackgroundStyle(background, screenSize)}>
      {renderContent()}
    </div>
  );

  function renderContent() {
    const WrapperComponent = fullWidth ? PageGrid : "div";
    const wrapperClass = fullWidth ? styles.layout : "";

    return (
      <WrapperComponent className={wrapperClass}>
        <div className={styles.content}>
          <div className={getContentFirstClass()}>
            {(align === "left" || align === "top") && renderGraphic()}
            {renderTextContent()}
          </div>
          <div className={styles.contentSecond}>
            {align === "right" && screenSize !== MEDIUM && renderGraphic()}
            {renderCallToAction()}
          </div>
          {(screenSize === SMALL || screenSize === MEDIUM) && renderDisclaimer()}
        </div>
      </WrapperComponent>
    );
  }

  function renderTextContent() {
    const { callout, primary, secondary } = text || {};

    return (
      <>
        <div className={styles.textContainer}>
          {renderText(callout)}
          <div className={styles.textHero}>
            {renderText(primary)}
            {renderText(secondary)}
          </div>
          {screenSize !== SMALL && screenSize !== MEDIUM && renderDisclaimer()}
        </div>
        {screenSize === MEDIUM && align === "right" && renderGraphic()}
      </>
    );
  }

  function renderGraphic() {
    const { altText, display, height, imagePath, width } = applyOverrides(graphic, screenSize);

    return (
      display !== "none" &&
      imagePath && <Image {...getImageProps({ altText, height, imagePath, width })} />
    );
  }

  function renderCallToAction() {
    let content = null;

    if (callToAction?.length) {
      content = callToAction.map((button) => (
        <CallToAction key={button.text} {...getCallToActionProps(button)} />
      ));
    } else if (callToAction) {
      content = <CallToAction {...getCallToActionProps(callToAction)} />;
    }

    return content;
  }

  function getCallToActionProps(ctaProps) {
    return {
      ...(screenSize === SMALL && { additionalProps: { fullWidth: true } }),
      ...ctaProps,
      ...placementData,
    };
  }

  function renderDisclaimer() {
    return disclaimer && <HtmlValue htmlValue={disclaimer?.value} />;
  }

  function getContentFirstClass() {
    return classNames({
      [styles.contentFirst]: !(screenSize === MEDIUM && align === "right"),
      [styles.contentFirstAlternate]: screenSize === MEDIUM && align === "right",
    });
  }

  function getImageProps(graphicOverrides = {}) {
    const { altText, height, imagePath, width } = graphicOverrides;

    return {
      alt: altText,
      className: styles.image,
      fitToContainer: true,
      height,
      src: imagePath,
      width,
    };
  }
};

BannerLayout1.displayName = "BannerLayout1";
BannerLayout1.propTypes = {
  /** Placement object for displayType banner. */
  placement: PropTypes.shape({
    /** Background image/color content for the banner. */
    background: backgroundPropType,

    /** Call To Action button / Link to be displayed in the banner placement. Can pass multiple. */
    callToAction: PropTypes.oneOfType([
      PropTypes.arrayOf(callToActionPropType),
      callToActionPropType,
    ]),

    /** Disclaimer text to be displayed in the banner placement. */
    disclaimer: PropTypes.shape({
      value: PropTypes.string,
    }),

    /** Graphic image to be displayed in the banner placement. */
    graphic: graphicPropType,

    /** Text content displayed in the banner placement. */
    text: PropTypes.shape({
      callout: textPropType,
      primary: textPropType,
      secondary: textPropType,
    }),
  }),

  placementData: PropTypes.shape({
    /** The dynamic value that will be used as an event description for analytics. */
    analyticsValue: PropTypes.string,
  }),
};
